import { ConsumationType, FlightPaymentType } from '@customTypes/types';
import {
  HomeIcon,
  MapPinIcon,
  PhotoIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';
import {
  Accordion,
  AccordionControl,
  Avatar,
  Flex,
  Space,
  Stack,
  Table,
  Text,
  Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PhotoList } from '@/features/work/components/photo-list/PhotoList';
import { useClubConditionView } from '@/features/work/view/club-full-profile/hooks/useClubConditionView';
import { PageLoading } from '@/pages/PageLoading';
import { resolveImagePath } from '@/shared/helper/resolveImagePath';
import { translate } from '@/shared/i18n/translate';
import { LBModalFullscreenLayout } from '@/shared/layouts';
import {
  LBAccordionContainer,
  LBBadge,
  LBLinkButton,
  LBListItem,
  LBModalHeader,
  LBNavLink
} from '@/shared/ui-kit/components';

import { VideoList } from '../../components/video-list/VideoList';

export const ClubFullProfile = ({ clubId }: { clubId: string }) => {
  const {
    state: {
      profileInfo,
      profilePhoto,
      profilePhotos,
      housingPhotos,
      isLoading,
      profileVideo
    }
  } = useClubConditionView({ clubId });

  const [photoModalStatus, { open: openPhotoModal, close: closePhotoModal }] =
    useDisclosure();
  const [videoModalStatus, { open: openVideoModal, close: closeVideoModal }] =
    useDisclosure();
  const [
    HousingModalStatus,
    { open: opeHousingoModal, close: closeHousingModal }
  ] = useDisclosure();
  const [
    CrazyMenuModalStatus,
    { open: openCrazyMenuModal, close: closeCrazyMenuModal }
  ] = useDisclosure();
  const [
    ConsumationModalStatus,
    { open: openConsumationModal, close: closeConsumationModal }
  ] = useDisclosure();
  const { formatTime } = useIntl();

  const currentCurrency = profileInfo.data?.data.clubInfo?.currencySymbol;

  const daysOrder = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const sortedData = React.useMemo(
    () =>
      [...(profileInfo.data?.data.schedule ?? [])].sort(
        (a, b) => daysOrder.indexOf(a.day) - daysOrder.indexOf(b.day)
      ),
    [profileInfo.data?.data.schedule]
  );

  if (isLoading) return <PageLoading />;

  return (
    <>
      {/* ClubPhoto */}
      <LBModalFullscreenLayout
        opened={photoModalStatus}
        onClose={closePhotoModal}
      >
        <LBModalHeader onClose={closePhotoModal} />
        <Title>{translate('commons.photo')}</Title>
        <Space h={24} />
        {!!profilePhotos?.data?.data && (
          <PhotoList data={profilePhotos?.data?.data} />
        )}
      </LBModalFullscreenLayout>
      {/* ClubPhoto */}
      {/* ClubVideo */}
      <LBModalFullscreenLayout
        opened={videoModalStatus}
        onClose={closeVideoModal}
      >
        <LBModalHeader onClose={closeVideoModal} />
        <Title>{translate('commons.video')}</Title>
        <Space h={24} />
        {!!profileVideo?.data?.data && (
          <VideoList data={profileVideo?.data?.data} />
        )}
      </LBModalFullscreenLayout>
      {/* ClubVideo */}
      {/* Housing Photo */}
      <LBModalFullscreenLayout
        opened={HousingModalStatus}
        onClose={closeHousingModal}
      >
        <LBModalHeader onClose={closeHousingModal} />
        <Title>{translate('commons.housing')}</Title>
        <Space h={24} />
        {housingPhotos?.data?.data.length && (
          <PhotoList data={housingPhotos.data.data} />
        )}
      </LBModalFullscreenLayout>
      {/* Housing Photo */}
      {/* CrazyMenu */}
      <LBModalFullscreenLayout
        opened={CrazyMenuModalStatus}
        onClose={closeCrazyMenuModal}
      >
        <LBModalHeader onClose={closeCrazyMenuModal} />
        <Title>{translate('commons.crazyMenu')}</Title>
        <Space h={20} />
        <Text fz={14}>
          {translate('pages.clubFullProfile.paymentFrequescyMessage')}{' '}
          <FormattedMessage
            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.crazyMenuPaymentFrequency}`}
          />
          .
        </Text>
        <Space h={20} />
        {!!profileInfo.data?.data.payments?.crazyMenu && (
          <Table>
            <Table.Thead bg='var(--base-light-main)'>
              <Table.Tr>
                <Table.Th>{translate('commons.position')}</Table.Th>
                <Table.Th>{translate('commons.guest')}</Table.Th>
                <Table.Th>{translate('commons.roles.artist')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {profileInfo.data?.data.payments?.crazyMenu.map((item) => (
                <Table.Tr key={item.name}>
                  <Table.Td>{item.name}</Table.Td>
                  <Table.Td>
                    {item.price} {currentCurrency}
                  </Table.Td>
                  {profileInfo.data?.data.payments?.crazyMenuPercent && (
                    <Table.Td>
                      {(item.price *
                        (profileInfo.data?.data.payments?.crazyMenuPercent ??
                          0)) /
                        100}{' '}
                      {currentCurrency}
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </LBModalFullscreenLayout>
      {/*  CrazyMenu */}
      {/* ConsumationMenu */}
      <LBModalFullscreenLayout
        opened={ConsumationModalStatus}
        onClose={closeConsumationModal}
      >
        <LBModalHeader onClose={closeConsumationModal} />
        <Title>{translate('commons.artistConsumation.label')}</Title>
        <Space h={20} />
        <Text fz={14}>
          {translate('pages.clubFullProfile.paymentFrequescyMessage')}{' '}
          <FormattedMessage
            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.consummationFrequency}`}
          />
          .
        </Text>
        <Space h={20} />
        {!!profileInfo.data?.data.payments?.consummationItems && (
          <Table>
            <Table.Thead bg='var(--base-light-main)'>
              <Table.Tr>
                <Table.Th>{translate('commons.position')}</Table.Th>
                <Table.Th>{translate('commons.guest')}</Table.Th>
                <Table.Th>{translate('commons.roles.artist')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {profileInfo.data?.data.payments?.consummationItems.map(
                (item) => (
                  <Table.Tr key={item.name}>
                    <Table.Td>{item.name}</Table.Td>
                    <Table.Td>
                      {item.price} {currentCurrency}
                    </Table.Td>
                    <Table.Td>
                      {item.reward} {currentCurrency}
                    </Table.Td>
                  </Table.Tr>
                )
              )}
            </Table.Tbody>
          </Table>
        )}
      </LBModalFullscreenLayout>
      {/*  ConsumationMenu */}

      <div>
        <Flex align='center' gap={16}>
          {!!profilePhoto.data?.data.length &&
          profilePhoto.data?.data[0].filePath ? (
            <Avatar
              size={120}
              src={resolveImagePath(profilePhoto.data?.data[0].filePath)}
            />
          ) : (
            <Avatar size={120}>
              {profileInfo.data?.data.clubInfo?.name.slice(0, 2)}
            </Avatar>
          )}
          <Flex direction='column' gap={8}>
            <Title order={4}>{profileInfo.data?.data.clubInfo?.name}</Title>
            <Flex align='start' gap={5}>
              <MapPinIcon width={18} />
              <Flex direction='column'>
                <Text fz={14}>
                  {profileInfo.data?.data.clubInfo?.country},{' '}
                </Text>{' '}
                <Text fz={14}>{profileInfo.data?.data.clubInfo?.address}</Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Space h={16} />
        <Flex direction='column'>
          {profileInfo.data?.data.clubInfo?.isHiring ? (
            <LBBadge c='green' color='green' variant='outline'>
              {translate('commons.artistHiringIsOpen')}
            </LBBadge>
          ) : (
            <LBBadge c='red' color='red' variant='outline'>
              {translate('commons.artistHiringIsClosed')}
            </LBBadge>
          )}
          <Space h={8} />
          <Flex direction='column' gap={4}>
            {profileInfo.data?.data?.workingConditions?.experienceRequired ? (
              <Text fw={700} fz={14}>
                {translate('commons.dontAcceptWithoutExperience')}
              </Text>
            ) : (
              <Text fw={700} fz={14}>
                {translate('commons.acceptWithExperience')}
              </Text>
            )}
            <Text fw={700} fz={14}>
              {translate('pages.clubFullProfile.artistWorkSchedule')}:{' '}
              {profileInfo.data?.data.contractTerms?.artistWorkSchedule}
            </Text>
            <Text fw={700} fz={14}>
              {translate('pages.clubFullProfile.minimalContractDuration')}:{' '}
              {profileInfo.data?.data.contractTerms?.minContractDurationWeeks}{' '}
              {translate('commons.paymentFrequency.Weekly')}
            </Text>
          </Flex>
        </Flex>
        <Space h={16} />
        <Stack gap={16}>
          <Stack gap={4}>
            <LBNavLink
              label={translate('pages.clubFullProfile.clubPhoto')}
              leftSection={<PhotoIcon width={18} />}
              onClick={openPhotoModal}
            />{' '}
            <LBNavLink
              label={translate('pages.clubFullProfile.clubVideo')}
              leftSection={<VideoCameraIcon width={18} />}
              onClick={openVideoModal}
            />
            <LBNavLink
              label={translate('pages.clubFullProfile.clubHousingPhoto')}
              leftSection={<HomeIcon width={18} />}
              onClick={opeHousingoModal}
            />
          </Stack>
          <LBAccordionContainer>
            <Accordion.Item value='payments'>
              <AccordionControl>
                <Title order={4}>{translate('commons.payments')}</Title>
              </AccordionControl>
              <Accordion.Panel>
                {!!profileInfo.data?.data.payments?.clubFee && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.clubFee')}</Text>
                    {profileInfo.data?.data.payments?.clubFee ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.clubFee}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments?.clubFreeFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.clubFreeFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.dailyRateMin && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.rate')}</Text>
                    {profileInfo.data?.data.payments?.dailyRateMin ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.dailyRateMin}
                        {currentCurrency}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.guarantee && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.guarantee')}</Text>
                    {profileInfo.data?.data.payments?.guarantee ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.guarantee}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.guaranteePaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.guaranteePaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.deposit && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.deposit')}</Text>
                    {profileInfo.data?.data.payments?.deposit ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.deposit}
                        {currentCurrency}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}

                {!!profileInfo.data?.data.payments?.show && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.show')}</Text>
                    {profileInfo.data?.data.payments?.show ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.show}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.showPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.showPaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.tips && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.tea')}</Text>
                    {profileInfo.data?.data.payments?.tips ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.tips} %
                        {/* /{' '}
                        {profileInfo.data?.data.payments
                          .tipsPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments.tipsPaymentFrequency}`}
                          />
                        )} */}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.consummationItems
                  .length && (
                  <LBListItem>
                    <Text fz={14}>
                      {translate('commons.artistConsumation.label')}
                    </Text>
                    <LBLinkButton onClick={() => openConsumationModal()}>
                      {translate('commons.open')}
                    </LBLinkButton>
                  </LBListItem>
                )}
                {!!profileInfo.data?.data.payments?.crazyMenu.length && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.crazyMenu')}</Text>
                    <LBLinkButton onClick={() => openCrazyMenuModal()}>
                      {translate('commons.open')}
                    </LBLinkButton>
                  </LBListItem>
                )}
                {(!!profileInfo.data?.data.payments?.topless ||
                  !!profileInfo.data?.data.payments?.full ||
                  !!profileInfo.data?.data.payments?.goGo) && (
                  <Text fz={14} mt={10}>
                    {translate('commons.separated')}:
                  </Text>
                )}

                {!!profileInfo.data?.data.payments?.topless && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.topless')}</Text>
                    {profileInfo.data?.data.payments?.topless ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.topless}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.toplessPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.toplessPaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}

                {!!profileInfo.data?.data.payments?.full && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.full')}</Text>
                    {profileInfo.data?.data.payments?.full ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.full}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.fullPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.fullPaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}

                {!!profileInfo.data?.data.payments?.goGo && (
                  <LBListItem>
                    <Text fz={14}>{translate('commons.artistJobs.GoGo')}</Text>
                    {profileInfo.data?.data.payments?.goGo ? (
                      <Text fz={14}>
                        {profileInfo.data?.data.payments?.goGo}
                        {currentCurrency} /{' '}
                        {profileInfo.data?.data.payments
                          ?.goGoPaymentFrequency && (
                          <FormattedMessage
                            id={`commons.paymentFrequency.${profileInfo.data?.data.payments?.goGoPaymentFrequency}`}
                          />
                        )}
                      </Text>
                    ) : (
                      <Text fz={11}>{translate('commons.no')}</Text>
                    )}
                  </LBListItem>
                )}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='work-hours'>
              <AccordionControl>
                <Title order={4}>{translate('commons.workingHours')}</Title>
              </AccordionControl>
              <Accordion.Panel>
                {sortedData.map((item) => (
                  <LBListItem key={item.day}>
                    <Text fz={14}>
                      <FormattedMessage id={`commons.schedule.${item.day}`} />
                    </Text>
                    {item.isClosed ? (
                      <Text fz={14}>{translate('commons.closed')}</Text>
                    ) : item.isOpen24Hours ? (
                      <Text fz={14}>{translate('commons.aroundTheClock')}</Text>
                    ) : (
                      <Text fz={12}>
                        {formatTime(
                          new Date().setHours(
                            +item.startHour.slice(0, 2),
                            +item.startHour.slice(3, 5)
                          )
                        )}{' '}
                        -{' '}
                        {formatTime(
                          new Date().setHours(
                            +item.closeHour.slice(0, 2),
                            +item.closeHour.slice(3, 5)
                          )
                        )}
                      </Text>
                    )}
                  </LBListItem>
                ))}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='housing'>
              <AccordionControl>
                <Title order={4}>{translate('commons.housing')}</Title>
                {profileInfo.data?.data.housing?.isHousingPaidByClub ? (
                  <Text fz={14}>{translate('commons.isPaydByClub')}</Text>
                ) : (
                  <Text fz={11}>{translate('commons.isntPaydByClub')}</Text>
                )}
              </AccordionControl>
              <Accordion.Panel>
                <LBListItem>
                  <Text fz={14}>{translate('commons.rentPayment')}</Text>
                  {profileInfo.data?.data.housing?.isHousingPaidByClub ? (
                    <Text fz={11} ta='center'>
                      {translate('commons.isPaydByClub')}
                    </Text>
                  ) : (
                    <Text fz={14}>
                      {profileInfo.data?.data.housing?.housingPrice}{' '}
                      {currentCurrency} /
                      {profileInfo.data?.data.housing
                        ?.housingPaymentFrequency && (
                        <FormattedMessage
                          id={`commons.paymentFrequency.${
                            profileInfo.data?.data.housing
                              ?.housingPaymentFrequency
                          }`}
                        />
                      )}
                    </Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.utilityPayments')}</Text>
                  {profileInfo.data?.data.housing?.isUtilityFeePaidByClub ? (
                    <Text fz={11}>{translate('commons.isPaydByClub')}</Text>
                  ) : (
                    <Text fz={14}>
                      {profileInfo.data?.data.housing?.utilityFee}{' '}
                      {currentCurrency} /{' '}
                      {profileInfo.data?.data.housing
                        ?.utilityPaymentFrequency && (
                        <FormattedMessage
                          id={`commons.paymentFrequency.${
                            profileInfo.data?.data.housing
                              ?.utilityPaymentFrequency
                          }`}
                        />
                      )}
                    </Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.peopleInRoom')}</Text>
                  <Text fz={14}>
                    {profileInfo.data?.data.housing?.peoplePerRoom}
                  </Text>
                </LBListItem>
                <Space h={8} />
                {profileInfo.data?.data.housing?.isClubHousingMandatory && (
                  <Text fw={700} fz={12}>
                    {translate('commons.housigInClubApartsRequired')}
                  </Text>
                )}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='ticket'>
              <AccordionControl>
                <Title order={4}>{translate('commons.flightTickets')}</Title>
              </AccordionControl>
              <Accordion.Panel>
                <LBListItem>
                  <Text fz={14}>
                    {translate('commons.transportFromAirport.label')}
                  </Text>
                  {profileInfo.data?.data.contractTerms
                    ?.isTransferOrganizedByClub ? (
                    <Text fz={14}>{translate('commons.yes')}</Text>
                  ) : (
                    <Text fz={14}>{translate('commons.no')}</Text>
                  )}
                </LBListItem>
                <Space h={8} />
                <LBListItem>
                  <Text fz={14}>{translate('commons.payment')}:</Text>
                  {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                    FlightPaymentType.Agreement && (
                    <Text fz={14}>
                      {' '}
                      {translate('commons.flightPaymentType.Agreement')}
                    </Text>
                  )}
                  {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                    FlightPaymentType.Artis && (
                    <Text fz={14}>
                      {translate('commons.flightPaymentType.Artist')}
                    </Text>
                  )}
                  {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                    FlightPaymentType.Club && (
                    <Text fz={14}>
                      {translate('commons.flightPaymentType.Club')}
                    </Text>
                  )}
                  {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                    FlightPaymentType.Credit && (
                    <Text fz={14}>
                      {translate('commons.flightPaymentType.Credit')}
                    </Text>
                  )}
                </LBListItem>

                {profileInfo.data?.data.contractTerms?.flightPaymentType ===
                  FlightPaymentType.Compensation && (
                  <Stack gap={8}>
                    <LBListItem>
                      <Text fz={14}>
                        {translate(
                          'commons.flightCompensationOptions.oneMonth'
                        )}
                      </Text>
                      <Text fz={14}>
                        {
                          profileInfo.data?.data.contractTerms
                            ?.compensationForOneMonth
                        }
                        %
                      </Text>
                    </LBListItem>
                    <LBListItem>
                      <Text fz={14}>
                        {' '}
                        {translate(
                          'commons.flightCompensationOptions.twoMonths'
                        )}
                      </Text>
                      <Text fz={14}>
                        {
                          profileInfo.data?.data.contractTerms
                            ?.compensationForTwoMonths
                        }
                        %
                      </Text>
                    </LBListItem>
                    <LBListItem>
                      <Text fz={14}>
                        {translate(
                          'commons.flightCompensationOptions.threeMonths'
                        )}
                      </Text>
                      <Text fz={14}>
                        {
                          profileInfo.data?.data.contractTerms
                            ?.compensationForThreeMonths
                        }
                        %
                      </Text>
                    </LBListItem>
                    <LBListItem>
                      <Text fz={14}>
                        {translate(
                          'commons.flightCompensationOptions.sixMonths'
                        )}
                      </Text>
                      {profileInfo.data?.data.contractTerms
                        ?.freeReturnTicketAfterSixMonths ? (
                        <Text fz={14}>
                          {translate(
                            'commons.flightCompensationOptions.backTicket'
                          )}
                        </Text>
                      ) : (
                        <Text fz={14}>{translate('commons.no')}</Text>
                      )}
                    </LBListItem>
                  </Stack>
                )}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='dress-code'>
              <AccordionControl>
                <Title order={4}>{translate('commons.dressCode')}</Title>
              </AccordionControl>
              <Accordion.Panel>
                {profileInfo.data?.data?.workingConditions?.dressCodes.map(
                  (item) => (
                    <LBListItem key={item}>
                      <Text fz={14}>{item}</Text>
                    </LBListItem>
                  )
                )}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='penalty'>
              <AccordionControl>
                <Title order={4}>{translate('commons.penalties')}</Title>
              </AccordionControl>
              <Accordion.Panel>
                {profileInfo.data?.data.penalties?.map((item) => (
                  <LBListItem key={item.penaltyItemId}>
                    <Text fz={14}>{item.name}</Text>
                    <Text fz={14}>
                      {item.fine} {currentCurrency}
                    </Text>
                  </LBListItem>
                ))}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='other'>
              <AccordionControl>
                <Title order={4}>{translate('commons.other')}</Title>
              </AccordionControl>
              <Accordion.Panel>
                <LBListItem>
                  <Text fz={14}>{translate('commons.teching')}</Text>
                  {profileInfo.data?.data?.workingConditions?.artistTraining ? (
                    <Text fz={14}>{translate('commons.yes')}</Text>
                  ) : (
                    <Text fz={14}>{translate('commons.no')}</Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>
                    {translate('commons.choreographerInClub')}
                  </Text>
                  {profileInfo.data?.data?.workingConditions
                    ?.choreographerPresence ? (
                    <Text fz={14}>{translate('commons.yes')}</Text>
                  ) : (
                    <Text fz={14}>{translate('commons.no')}</Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>
                    {translate('commons.artistConsumation.label')}
                  </Text>
                  {profileInfo.data?.data?.workingConditions
                    ?.consummationType === ConsumationType.Active ? (
                    <Text fz={14}>
                      {translate('commons.artistConsumation.Active')}
                    </Text>
                  ) : (
                    <Text fz={14}>
                      {translate('commons.artistConsumation.Passive')}
                    </Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>
                    {translate('commons.photoshootParticipation')}
                  </Text>
                  {profileInfo.data?.data?.workingConditions
                    ?.photoshootMandatory ? (
                    <Text fz={14}>{translate('commons.required')}</Text>
                  ) : (
                    <Text fz={14}>{translate('commons.optional')}</Text>
                  )}
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.artistsInShift')}</Text>
                  <Text fz={14}>
                    {profileInfo.data?.data?.workingConditions?.artistsPerShift}
                  </Text>
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.scenes')}</Text>
                  <Text fz={14}>
                    {profileInfo.data?.data?.workingConditions?.numberOfStages}
                  </Text>
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.dynamicPole')}</Text>
                  <Text fz={14}>
                    {
                      profileInfo.data?.data?.workingConditions
                        ?.numberOfPolesDynamic
                    }
                  </Text>
                </LBListItem>
                <LBListItem>
                  <Text fz={14}>{translate('commons.staticPole')}</Text>
                  <Text fz={14}>
                    {
                      profileInfo.data?.data?.workingConditions
                        ?.numberOfPolesStatic
                    }
                  </Text>
                </LBListItem>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value='about'>
              <AccordionControl>
                <Title order={4}>{translate('commons.aboutClub')}</Title>
              </AccordionControl>
              <Accordion.Panel>
                <Text fz={14} style={{ whiteSpace: 'pre-wrap' }}>
                  {
                    profileInfo.data?.data?.workingConditions
                      ?.additionalInformation
                  }
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </LBAccordionContainer>
        </Stack>
      </div>
    </>
  );
};
